import { Link, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { useState } from "react";
import type { Brand } from "../../../shared/types";
import { Markdown } from "../components/agent/Markdown";
import { useAddToCorpus } from "../hooks/useBrand";
import { useCreateResearchPackage, useResearchRun } from "../hooks/useMarketResearch";
import { buildCorpusPath } from "../lib/brand-navigation";
import { WorkspaceHeader } from "../components/layout/WorkspaceHeader";

type BrandOutletContext = {
  brand: Brand;
  brandId: string;
};

function statusBadge(status: string) {
  const color =
    status === "completed" ? "bg-green-100 text-green-800"
    : status === "approved" ? "bg-green-100 text-green-800"
    : status === "pending" ? "bg-yellow-100 text-yellow-800"
    : "bg-slate-100 text-slate-600";
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${color}`}>
      {status}
    </span>
  );
}

function MetricCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg bg-slate-50 px-4 py-3">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-2xl font-bold text-slate-900">{value}</div>
    </div>
  );
}

export function ResearchRunDetail() {
  const { brand, brandId: outletBrandId } = useOutletContext<BrandOutletContext>();
  const { brandId: routeBrandId, runId } = useParams<{ brandId: string; runId: string }>();
  const brandId = routeBrandId ?? outletBrandId;
  const navigate = useNavigate();
  const { data, isLoading, isError } = useResearchRun(brandId, runId);
  const createResearchPackage = useCreateResearchPackage(brandId);
  const addToCorpus = useAddToCorpus(brandId);
  const [packageError, setPackageError] = useState<string | null>(null);
  const run = data?.data;

  if (isLoading) {
    return (
      <div className="flex min-h-[120px] items-center justify-center">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  if (isError || !run) {
    return <p className="text-sm text-red-600">Could not load run details.</p>;
  }

  const resolvedRun = run;
  const reportMd = resolvedRun.report_md?.trim() ?? "";
  const canSaveReportToCorpus = reportMd.length > 0;
  const saveReportHelpId = "save-report-to-corpus-help";

  async function handleSaveToCorpus() {
    if (!canSaveReportToCorpus) return;

    await addToCorpus.mutateAsync({
      content: [
        `# Market Research Report`,
        `Brand: ${brand.name}`,
        `Run date: ${resolvedRun.run_date}`,
        resolvedRun.topic ? `Topic: ${resolvedRun.topic}` : null,
        "",
        reportMd,
      ]
        .filter(Boolean)
        .join("\n"),
      metadata: {
        source: "edit",
        source_type: "market_research",
        run_id: resolvedRun.id,
        topic: resolvedRun.topic,
      },
    });
  }

  async function handleCreateResearchPackage() {
    if (!brandId || !runId) return;
    setPackageError(null);
    try {
      const result = await createResearchPackage.mutateAsync({ runId });
      navigate(`/brand/${brandId}/research/packages/${result.data.id}`);
    } catch (error) {
      setPackageError(error instanceof Error ? error.message : "Could not create a research package.");
    }
  }

  return (
    <div className="space-y-6">
      <WorkspaceHeader
        eyebrow="Research Document"
        title={resolvedRun.topic ?? resolvedRun.run_date}
        description={`This Run is the first durable Result from the market research Action. Review the report, preserve useful findings in Corpus, and convert it into a structured package when it is strong enough to drive downstream strategy artifacts.`}
        breadcrumbs={[
          { label: "Actions", href: "/tasks" },
          { label: "Research Documents", href: `/brand/${brandId}/research` },
          { label: resolvedRun.topic ?? resolvedRun.run_date },
        ]}
        badges={(
          <>
            <span className="rounded-full border border-brand-200 bg-brand-50 px-3 py-1 text-xs font-semibold text-brand-700">
              {brand.name}
            </span>
            {statusBadge(resolvedRun.status)}
          </>
        )}
        metadata={(
          <>
            <span className="rounded-full bg-white px-2.5 py-1 ring-1 ring-slate-200">
              {resolvedRun.run_date}
            </span>
            <span className="rounded-full bg-white px-2.5 py-1 ring-1 ring-slate-200">
              {resolvedRun.keywords_researched_count} keywords
            </span>
          </>
        )}
        actions={(
          <>
            <Link
              to={`/brand/${brandId}/campaigns`}
              state={{ fromResearch: true, researchTopic: resolvedRun.topic ?? resolvedRun.run_date }}
              className="inline-flex items-center justify-center rounded-xl bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
            >
              Create campaign
            </Link>
            <button
              type="button"
              onClick={() => void handleCreateResearchPackage()}
              disabled={createResearchPackage.isPending}
              className="inline-flex items-center justify-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-400 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {createResearchPackage.isPending ? "Creating package…" : "Create Research Package"}
            </button>
            <button
              type="button"
              onClick={() => void handleSaveToCorpus()}
              disabled={addToCorpus.isPending || !canSaveReportToCorpus}
              aria-describedby={saveReportHelpId}
              className="inline-flex items-center justify-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-400 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {addToCorpus.isPending ? "Saving…" : "Save report to Corpus"}
            </button>
            <Link
              to="/tasks"
              className="inline-flex items-center justify-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-400 hover:text-slate-900"
            >
              Open Actions
            </Link>
          </>
        )}
      />

      <div className="rounded-[20px] border border-brand-100 bg-brand-50 p-4 shadow-sm">
        <h2 className="text-sm font-semibold text-slate-900">Report actions</h2>
        <p id={saveReportHelpId} className="mt-1 text-sm text-slate-700">
          {canSaveReportToCorpus
            ? "Save the markdown report to Corpus when you want future briefs and agents to reuse these findings."
            : "Save report to Corpus is disabled because this run does not have a report body yet."}
        </p>
        <p className="mt-1 text-xs text-slate-600">
          Launch a fresh market research Run from Actions when the market, positioning, or target keyword set has changed.
        </p>
      </div>

      {addToCorpus.isSuccess && (
        <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
          Report saved to{" "}
          <Link to={buildCorpusPath(brandId)} className="font-medium underline">
            Corpus
          </Link>
          .
        </div>
      )}

      {addToCorpus.isError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {addToCorpus.error instanceof Error ? addToCorpus.error.message : "Could not save this report to Corpus."}
        </div>
      )}

      {packageError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {packageError}
        </div>
      )}

      <section>
        <div className="mb-2 flex flex-wrap items-end justify-between gap-2">
          <div>
            <h2 className="text-xs font-medium uppercase tracking-wide text-slate-500">Research Report</h2>
            <p className="mt-1 text-sm text-slate-600">
              Primary work product from this run. Metrics and seed keywords are supporting context below.
            </p>
          </div>
        </div>
        {reportMd ? (
          <div className="overflow-x-auto rounded-[20px] border border-slate-200 bg-white p-4 shadow-sm">
            <Markdown content={reportMd} titleAlreadyShown={resolvedRun.topic ?? resolvedRun.run_date} />
          </div>
        ) : (
          <div className="rounded-[20px] border border-dashed border-slate-200 bg-white p-4 text-sm text-slate-600 shadow-sm">
            No report body was saved for this market-research run. Re-run the research when you need a reusable report.
          </div>
        )}
      </section>

      <section>
        <h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">Summary Metrics</h2>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <MetricCard label="Opportunities" value={resolvedRun.opportunities_count} />
          <MetricCard label="Content Ideas" value={resolvedRun.content_ideas_count} />
          <MetricCard label="Threats" value={resolvedRun.threats_count} />
          <MetricCard label="Keywords" value={resolvedRun.keywords_researched_count} />
        </div>
      </section>

      {resolvedRun.keyword_seeds && resolvedRun.keyword_seeds.length > 0 && (
        <section>
          <h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">Seed Keywords</h2>
          <div className="flex flex-wrap gap-2">
            {resolvedRun.keyword_seeds.map((keyword) => (
              <span
                key={keyword}
                className="rounded-full bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-700"
              >
                {keyword}
              </span>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
